import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	Alert,
	Button,
	Callout,
	Card,
	Collapse,
	Divider,
	FormGroup,
	Icon,
	InputGroup,
	Intent,
	NonIdealState,
	Spinner,
	Tag,
	TextArea,
} from "@blueprintjs/core";

import { IStore } from "redux/store";
import { commonUtils } from "redux/_common";
import authConstants from "redux/app/auth/constants";

type FAQQuestion = { question: string; answer: string };
type FAQSection = { title: string; questions: FAQQuestion[] };
type VersionHistoryEntry = {
	fromVersion: string | null;
	toVersion: string;
	bump: "none" | "patch" | "minor" | "major";
	at: string;
	by?: { id: string; name?: string };
	summary?: string;
	counts?: { sections: number; questions: number };
};
type FAQDoc = {
	_id: "faq";
	_rev?: string;
	modelName?: "faq";
	version?: string;
	faqs: FAQSection[];
	versionHistory?: VersionHistoryEntry[];
};

type OwnProps = {};
type FaqManagerProps = PropsFromRedux & OwnProps;

function FaqManager(props: FaqManagerProps) {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const [doc, setDoc] = useState<FAQDoc | null>(null);
	const [draftFaqs, setDraftFaqs] = useState<FAQSection[]>([]);
	const [search, setSearch] = useState("");
	const [selectedSectionIdx, setSelectedSectionIdx] = useState<number>(0);
	const [expandedQuestionKey, setExpandedQuestionKey] = useState<string | null>(null);
	const [initialFaqsJson, setInitialFaqsJson] = useState<string>("[]");
	const [showHistory, setShowHistory] = useState(false);

	const [confirmDeleteSectionIdx, setConfirmDeleteSectionIdx] = useState<number | null>(
		null
	);
	const [confirmDeleteQuestion, setConfirmDeleteQuestion] = useState<{
		sectionIdx: number;
		questionIdx: number;
	} | null>(null);
	const [confirmRefreshDirty, setConfirmRefreshDirty] = useState(false);

	const normalizedSearch = search.trim().toLowerCase();

	const fetchFaq = useCallback(async () => {
		setLoading(true);
		setErrorMessage(null);
		setSuccessMessage(null);
		try {
			const resp = await fetch(`${commonUtils.getAPIBaseUrl()}/api/faq`, {
				headers: {
					[authConstants.headers.TOKEN]: props.authToken || "",
				},
			});
			const json = (await resp.json()) as any;
			if (!resp.ok) {
				setErrorMessage(json?.message || "Failed to load FAQ");
				setDoc(null);
				setDraftFaqs([]);
				setInitialFaqsJson("[]");
				setSelectedSectionIdx(0);
				setExpandedQuestionKey(null);
				return;
			}
			const loaded = json as FAQDoc;
			setDoc(loaded);
			const faqs = Array.isArray(loaded?.faqs) ? loaded.faqs : [];
			setDraftFaqs(faqs);
			setInitialFaqsJson(JSON.stringify(faqs));
			setSelectedSectionIdx(0);
			setExpandedQuestionKey(null);
		} catch (e: any) {
			setErrorMessage(e?.message || "Network error");
		} finally {
			setLoading(false);
		}
	}, [props.authToken]);

	useEffect(() => {
		fetchFaq();
	}, [fetchFaq]);

	const isDirty = useMemo(() => {
		return JSON.stringify(draftFaqs) !== initialFaqsJson;
	}, [draftFaqs, initialFaqsJson]);

	const visibleSections = useMemo(() => {
		return draftFaqs
			.map((section, index) => {
				if (!normalizedSearch) return { index, section, matchCount: 0 };
				const title = (section.title || "").toLowerCase();
				let matchCount = title.includes(normalizedSearch) ? 1 : 0;
				for (const q of section.questions || []) {
					const qq = (q.question || "").toLowerCase();
					const aa = (q.answer || "").toLowerCase();
					if (qq.includes(normalizedSearch) || aa.includes(normalizedSearch)) {
						matchCount += 1;
					}
				}
				if (matchCount === 0) return null;
				return { index, section, matchCount };
			})
			.filter(Boolean) as Array<{ index: number; section: FAQSection; matchCount: number }>;
	}, [draftFaqs, normalizedSearch]);

	useEffect(() => {
		if (draftFaqs.length === 0) {
			setSelectedSectionIdx(0);
			return;
		}
		if (selectedSectionIdx < 0 || selectedSectionIdx >= draftFaqs.length) {
			setSelectedSectionIdx(0);
			return;
		}
	}, [draftFaqs.length, selectedSectionIdx]);

	const setSectionTitle = (sectionIdx: number, title: string) => {
		setDraftFaqs((prev) =>
			prev.map((s, i) => (i === sectionIdx ? { ...s, title } : s))
		);
	};

	const addSection = () => {
		setDraftFaqs((prev) => {
			const next = [...prev, { title: "New Section", questions: [] }];
			// Ensure we select the newly-added section (avoid stale closure on draftFaqs.length)
			setSelectedSectionIdx(next.length - 1);
			setExpandedQuestionKey(null);
			return next;
		});
	};

	const deleteSection = (sectionIdx: number) => {
		setDraftFaqs((prev) => prev.filter((_, i) => i !== sectionIdx));
		setSelectedSectionIdx((prevSelected) => {
			if (prevSelected > sectionIdx) return prevSelected - 1;
			if (prevSelected === sectionIdx) return 0;
			return prevSelected;
		});
		setExpandedQuestionKey(null);
	};

	const moveSection = (from: number, to: number) => {
		setDraftFaqs((prev) => {
			if (to < 0 || to >= prev.length) return prev;
			const copy = [...prev];
			const [item] = copy.splice(from, 1);
			copy.splice(to, 0, item);
			return copy;
		});
		setSelectedSectionIdx((prevSelected) => {
			if (prevSelected === from) return to;
			if (from < prevSelected && prevSelected <= to) return prevSelected - 1;
			if (to <= prevSelected && prevSelected < from) return prevSelected + 1;
			return prevSelected;
		});
	};

	const addQuestion = (sectionIdx: number) => {
		setDraftFaqs((prev) =>
			prev.map((s, i) =>
				i === sectionIdx
					? {
							...s,
							questions: [...(s.questions || []), { question: "", answer: "" }],
					  }
					: s
			)
		);
		const nextIdx = (draftFaqs[sectionIdx]?.questions?.length ?? 0);
		setExpandedQuestionKey(`${sectionIdx}-${nextIdx}`);
	};

	const deleteQuestion = (sectionIdx: number, questionIdx: number) => {
		setDraftFaqs((prev) =>
			prev.map((s, i) =>
				i === sectionIdx
					? {
							...s,
							questions: (s.questions || []).filter((_, qi) => qi !== questionIdx),
					  }
					: s
			)
		);
		if (expandedQuestionKey === `${sectionIdx}-${questionIdx}`) setExpandedQuestionKey(null);
	};

	const moveQuestion = (sectionIdx: number, from: number, to: number) => {
		setDraftFaqs((prev) =>
			prev.map((s, i) => {
				if (i !== sectionIdx) return s;
				const qs = [...(s.questions || [])];
				if (to < 0 || to >= qs.length) return s;
				const [item] = qs.splice(from, 1);
				qs.splice(to, 0, item);
				return { ...s, questions: qs };
			})
		);
		setExpandedQuestionKey((prevKey) => {
			if (!prevKey) return prevKey;
			const [sIdx, qIdx] = prevKey.split("-").map((n) => Number(n));
			if (sIdx !== sectionIdx) return prevKey;
			if (qIdx === from) return `${sectionIdx}-${to}`;
			return prevKey;
		});
	};

	const duplicateQuestion = (sectionIdx: number, questionIdx: number) => {
		setDraftFaqs((prev) =>
			prev.map((s, i) => {
				if (i !== sectionIdx) return s;
				const qs = [...(s.questions || [])];
				const original = qs[questionIdx];
				if (!original) return s;
				qs.splice(questionIdx + 1, 0, { ...original });
				return { ...s, questions: qs };
			})
		);
		setExpandedQuestionKey(`${sectionIdx}-${questionIdx + 1}`);
	};

	const setQuestionField = (
		sectionIdx: number,
		questionIdx: number,
		field: "question" | "answer",
		value: string
	) => {
		setDraftFaqs((prev) =>
			prev.map((s, i) => {
				if (i !== sectionIdx) return s;
				const questions = (s.questions || []).map((q, qi) =>
					qi === questionIdx ? { ...q, [field]: value } : q
				);
				return { ...s, questions };
			})
		);
	};

	const validateDraft = () => {
		for (let i = 0; i < draftFaqs.length; i++) {
			const s = draftFaqs[i];
			if (!s.title?.trim()) return `Section ${i + 1}: title is required`;
			for (let j = 0; j < (s.questions || []).length; j++) {
				const q = s.questions[j];
				if (!q.question?.trim()) return `Section ${i + 1}, Q${j + 1}: question is required`;
				if (!q.answer?.trim()) return `Section ${i + 1}, Q${j + 1}: answer is required`;
			}
		}
		return null;
	};

	const saveFaq = async () => {
		setSaving(true);
		setErrorMessage(null);
		setSuccessMessage(null);

		const validationError = validateDraft();
		if (validationError) {
			setErrorMessage(validationError);
			setSaving(false);
			return;
		}

		try {
			const resp = await fetch(`${commonUtils.getAPIBaseUrl()}/api/faq`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					[authConstants.headers.TOKEN]: props.authToken || "",
				},
				body: JSON.stringify({
					faqs: draftFaqs,
					version: doc?.version,
				}),
			});
			const json = (await resp.json()) as any;
			if (!resp.ok) {
				setErrorMessage(json?.message || "Failed to save FAQ");
				return;
			}
			setSuccessMessage("Saved");
			// refresh to pick up latest _rev + server audit fields
			await fetchFaq();
		} catch (e: any) {
			setErrorMessage(e?.message || "Network error");
		} finally {
			setSaving(false);
		}
	};

	const hasData = !!doc && !loading && !errorMessage;
	const selectedSection = draftFaqs[selectedSectionIdx];
	const versionHistorySorted = useMemo(() => {
		const history = Array.isArray(doc?.versionHistory) ? doc!.versionHistory! : [];
		return [...history].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
	}, [doc?.versionHistory]);

	const formatLocalDateTime = (iso?: string | null) => {
		if (!iso) return "";
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return iso;
		return d.toLocaleString(undefined, {
			year: "numeric",
			month: "short",
			day: "2-digit",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};
	const selectedSectionTitleMatches = useMemo(() => {
		if (!normalizedSearch) return false;
		return ((selectedSection?.title || "").toLowerCase() || "").includes(normalizedSearch);
	}, [normalizedSearch, selectedSection?.title]);

	const visibleQuestions = useMemo(() => {
		const qs = selectedSection?.questions || [];
		if (!normalizedSearch) {
			return qs.map((q, index) => ({ q, index, isMatch: false }));
		}
		return qs
			.map((q, index) => {
				const qq = (q.question || "").toLowerCase();
				const aa = (q.answer || "").toLowerCase();
				const isMatch = qq.includes(normalizedSearch) || aa.includes(normalizedSearch);
				return { q, index, isMatch };
			})
			.filter(({ isMatch }) => selectedSectionTitleMatches || isMatch);
	}, [normalizedSearch, selectedSection?.questions, selectedSectionTitleMatches]);

	const handleRefresh = () => {
		if (isDirty) {
			setConfirmRefreshDirty(true);
			return;
		}
		fetchFaq();
	};

	const discardChanges = () => {
		try {
			const parsed = JSON.parse(initialFaqsJson) as FAQSection[];
			setDraftFaqs(Array.isArray(parsed) ? parsed : []);
			setExpandedQuestionKey(null);
			setErrorMessage(null);
			setSuccessMessage(null);
		} catch {
			fetchFaq();
		}
	};

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gray-50 p-6 md:p-8 lg:p-12">
			<div className="max-w-[1600px] mx-auto space-y-6">
				<div className="flex items-center justify-between gap-4 flex-wrap">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
							<Icon icon="help" size={28} className="text-blue-600" />
							FAQ Manager
						</h1>
						<p className="text-gray-600">
							Edit the FAQ document.
						</p>
					</div>

					<div className="flex items-center gap-2">
						<Tag intent={isDirty ? Intent.WARNING : Intent.SUCCESS}>
							{isDirty ? "Unsaved changes" : "All changes saved"}
						</Tag>
						{doc?._rev && (
							<Tag minimal className="font-mono">
								_rev: {doc._rev}
							</Tag>
						)}
						{doc?.version && (
							<Tag minimal className="font-mono">
								version: {doc.version}
							</Tag>
						)}
						<Button
							icon="history"
							minimal
							disabled={loading || saving || (doc?.versionHistory?.length ?? 0) === 0}
							onClick={() => setShowHistory((v) => !v)}
							title="Toggle version history"
						>
							History
						</Button>
						<Button
							icon="undo"
							intent={Intent.NONE}
							disabled={loading || saving || !isDirty}
							onClick={discardChanges}
						>
							Discard
						</Button>
						<Button
							icon="refresh"
							intent={Intent.NONE}
							disabled={loading || saving}
							loading={loading}
							onClick={handleRefresh}
						>
							Refresh
						</Button>
						<Button
							icon="floppy-disk"
							intent={Intent.PRIMARY}
							disabled={loading || saving || !isDirty}
							loading={saving}
							onClick={saveFaq}
						>
							Save
						</Button>
					</div>
				</div>

				{errorMessage && (
					<Callout intent={Intent.DANGER} icon="error" title="Error">
						{errorMessage}
					</Callout>
				)}
				{successMessage && (
					<Callout intent={Intent.SUCCESS} icon="tick" title="Success">
						{successMessage}
					</Callout>
				)}

				<Collapse isOpen={showHistory && (doc?.versionHistory?.length ?? 0) > 0}>
					<Card elevation={0} className="border border-gray-100 bg-white rounded-2xl overflow-hidden">
						<div className="p-4">
							<div className="flex items-center justify-between gap-3 flex-wrap">
								<div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
									<Icon icon="history" className="text-gray-500" />
									Version history
									<Tag minimal>{doc?.versionHistory?.length ?? 0}</Tag>
								</div>
								<Button minimal icon="cross" onClick={() => setShowHistory(false)}>
									Close
								</Button>
							</div>

							<div className="mt-3 max-h-[320px] overflow-auto border border-gray-100 rounded-xl">
								<ul className="divide-y divide-gray-100">
									{versionHistorySorted.map((h, idx) => (
										<li key={`${h.toVersion}-${h.at}-${idx}`} className="px-4 py-3">
											<div className="flex items-start justify-between gap-3 flex-wrap">
												<div className="min-w-0">
													<div className="text-sm font-semibold text-gray-900 break-words">
														{h.fromVersion ? `${h.fromVersion} → ` : ""}
														{h.toVersion}
														{h.bump && h.bump !== "none" ? ` (${h.bump})` : ""}
													</div>
													<div className="text-xs text-gray-600 mt-0.5 break-words">
														{h.summary || "Updated"}
													</div>
													<div className="text-xs text-gray-500 mt-1">
														{formatLocalDateTime(h.at)}
														{h.by?.name ? ` • ${h.by.name}` : h.by?.id ? ` • ${h.by.id}` : ""}
														{h.counts
															? ` • ${h.counts.sections} sections, ${h.counts.questions} questions`
															: ""}
													</div>
												</div>
												<Tag minimal className="font-mono">
													{h.toVersion}
												</Tag>
											</div>
										</li>
									))}
								</ul>
							</div>
						</div>
					</Card>
				</Collapse>

				{loading ? (
					<div className="py-12 flex justify-center">
						<Spinner size={40} />
					</div>
				) : !doc && !errorMessage ? (
					<NonIdealState
						icon="help"
						title="FAQ not loaded"
						description="Click Refresh to load the FAQ document."
						className="py-12 bg-white rounded-lg shadow-sm border border-gray-100"
					/>
				) : !hasData ? null : visibleSections.length === 0 ? (
					<NonIdealState
						icon="search"
						title="No matches"
						description="Try a different search term."
						className="py-12 bg-white rounded-lg shadow-sm border border-gray-100"
					/>
				) : (
					<div className="flex flex-col md:flex-row gap-6 items-start">
						<Card
							elevation={1}
							className="bg-white md:sticky md:top-6 border border-gray-100 rounded-2xl shadow-sm md:w-1/4 md:shrink-0 w-full"
						>
							<div className="p-4 space-y-4">
								<div className="flex items-center justify-between gap-3">
									<div className="flex items-center gap-2 min-w-0">
										<Icon icon="properties" className="text-gray-500" />
										<div className="font-semibold text-gray-900">Sections</div>
										<Tag minimal>
											{normalizedSearch
												? `${visibleSections.length}/${draftFaqs.length}`
												: draftFaqs.length}
										</Tag>
									</div>
									<Button
										icon="add"
										intent={Intent.SUCCESS}
										small
										disabled={saving}
										onClick={addSection}
									>
										New
									</Button>
								</div>

								<FormGroup label="Search" className="mb-0">
									<InputGroup
										leftIcon="search"
										placeholder="Search sections/questions..."
										value={search}
										disabled={saving}
										rightElement={
											search ? (
												<Button
													icon="cross"
													minimal
													disabled={saving}
													onClick={() => setSearch("")}
													title="Clear search"
												/>
											) : undefined
										}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											setSearch(e.target.value)
										}
									/>
								</FormGroup>

								<div className="max-h-[65vh] overflow-auto border border-gray-100 rounded-xl bg-white">
									<ul className="p-2 space-y-1">
										{visibleSections.map(({ index, section, matchCount }) => {
											const isSelected = index === selectedSectionIdx;
											return (
												<li key={`section-${index}`}>
													<button
														type="button"
														disabled={saving}
														className={[
															"w-full text-left rounded-xl px-3 py-2 transition border",
															"focus:outline-none focus:ring-2 focus:ring-blue-400/60",
															isSelected
																? "bg-blue-50 border-blue-200"
																: "bg-white border-transparent hover:bg-gray-50 hover:border-gray-100",
														].join(" ")}
														onClick={() => {
															setSelectedSectionIdx(index);
															setExpandedQuestionKey(null);
														}}
													>
														<div className="flex items-start justify-between gap-3">
															<div className="min-w-0">
																<div className="text-sm font-semibold text-gray-900 truncate">
																	{section.title || "Untitled section"}
																</div>
																<div className="text-xs text-gray-600 mt-0.5 flex flex-wrap gap-x-2 gap-y-1">
																	<span>
																		{section.questions?.length ?? 0} question
																		{(section.questions?.length ?? 0) === 1 ? "" : "s"}
																	</span>
																	{normalizedSearch && matchCount > 0 && (
																		<span className="text-gray-500">
																			• {matchCount} match
																			{matchCount === 1 ? "" : "es"}
																		</span>
																	)}
																</div>
															</div>
															{/* Selected styling is handled by the row border/background */}
														</div>
													</button>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
						</Card>

						<Card
							elevation={1}
							className="bg-white border border-gray-100 rounded-2xl shadow-sm min-w-0 w-full md:w-3/4 overflow-hidden"
						>
							<div className="p-4 space-y-4 overflow-x-hidden">
								{!selectedSection ? (
									<NonIdealState
										icon="help"
										title="Select a section"
										description="Choose a section from the left to start editing."
										className="py-12"
									/>
								) : (
									<>
										<div className="flex items-start justify-between gap-3 flex-wrap">
											<div className="grow min-w-[260px]">
												<FormGroup label="Section title" labelInfo="(required)" className="mb-0">
													<InputGroup
														value={selectedSection.title}
														disabled={saving}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
															setSectionTitle(selectedSectionIdx, e.target.value)
														}
													/>
												</FormGroup>
											</div>
											<div className="flex items-center gap-1">
												<Button
													icon="arrow-up"
													minimal
													small
													disabled={saving || selectedSectionIdx === 0}
													onClick={() => moveSection(selectedSectionIdx, selectedSectionIdx - 1)}
												/>
												<Button
													icon="arrow-down"
													minimal
													small
													disabled={saving || selectedSectionIdx === draftFaqs.length - 1}
													onClick={() => moveSection(selectedSectionIdx, selectedSectionIdx + 1)}
												/>
												<Button
													icon="trash"
													intent={Intent.DANGER}
													minimal
													small
													disabled={saving}
													onClick={() => setConfirmDeleteSectionIdx(selectedSectionIdx)}
												/>
											</div>
										</div>

										<Divider />

										<div className="flex items-center justify-between gap-3 flex-wrap">
											<div className="text-sm text-gray-600 flex items-center gap-2">
												<Icon icon="help" className="text-gray-500" />
												<div>
													<span className="font-medium text-gray-800">Questions</span>{" "}
													<span className="text-gray-500">
														{normalizedSearch
															? `(${visibleQuestions.length}/${
																	selectedSection.questions?.length ?? 0
															  })`
															: `(${selectedSection.questions?.length ?? 0})`}
													</span>
												</div>
											</div>
											<Button
												icon="add"
												intent={Intent.PRIMARY}
												disabled={saving}
												onClick={() => addQuestion(selectedSectionIdx)}
											>
												Add question
											</Button>
										</div>

										{(selectedSection.questions || []).length === 0 ? (
											<NonIdealState
												icon="help"
												title="No questions yet"
												description="Add your first question."
												className="py-10 border border-gray-100 rounded-lg"
											/>
										) : normalizedSearch && visibleQuestions.length === 0 ? (
											<NonIdealState
												icon="search"
												title="No matching questions"
												description="Try a different search term, or select a different section."
												className="py-10 border border-gray-100 rounded-lg"
											/>
										) : (
											<div className="space-y-3">
												{visibleQuestions.map(({ q, index: questionIdx }) => {
													const key = `${selectedSectionIdx}-${questionIdx}`;
													const isOpen = expandedQuestionKey === key;
													const headerText = q.question?.trim()
														? q.question.trim()
														: `Q${questionIdx + 1} (click to edit)`;

													return (
														<Card
															key={key}
															elevation={0}
															className="border border-gray-100 overflow-hidden"
														>
															<div className="p-3">
																<div
																	className="flex items-center justify-between gap-3 cursor-pointer"
																	onClick={() =>
																		setExpandedQuestionKey((prev) => (prev === key ? null : key))
																	}
																>
																	<div className="min-w-0">
																		<div className="text-sm font-semibold text-gray-900 break-words">
																			{headerText}
																		</div>
																		{!isOpen && q.answer?.trim() && (
																			<div className="text-xs text-gray-600 break-words">
																				{q.answer.trim()}
																			</div>
																		)}
																	</div>
																	<Button
																		icon={isOpen ? "chevron-up" : "chevron-down"}
																		minimal
																	/>
																</div>

																<Collapse isOpen={isOpen}>
																	<div className="pt-3 space-y-3">
																		<div className="flex items-center justify-end gap-2">
																			<Button
																				icon="arrow-up"
																				minimal
																				disabled={saving || questionIdx === 0}
																				onClick={() =>
																					moveQuestion(
																						selectedSectionIdx,
																						questionIdx,
																						questionIdx - 1
																					)
																				}
																			/>
																			<Button
																				icon="arrow-down"
																				minimal
																				disabled={
																					saving ||
																					questionIdx === (selectedSection.questions || []).length - 1
																				}
																				onClick={() =>
																					moveQuestion(
																						selectedSectionIdx,
																						questionIdx,
																						questionIdx + 1
																					)
																				}
																			/>
																			<Button
																				icon="duplicate"
																				minimal
																				disabled={saving}
																				onClick={() => duplicateQuestion(selectedSectionIdx, questionIdx)}
																			/>
																			<Button
																				icon="trash"
																				intent={Intent.DANGER}
																				minimal
																				disabled={saving}
																				onClick={() =>
																					setConfirmDeleteQuestion({
																						sectionIdx: selectedSectionIdx,
																						questionIdx,
																					})
																				}
																			/>
																		</div>

																		<FormGroup label="Question" className="mb-0">
																			<InputGroup
																				value={q.question}
																				disabled={saving}
																				onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
																					setQuestionField(
																						selectedSectionIdx,
																						questionIdx,
																						"question",
																						e.target.value
																					)
																				}
																			/>
																		</FormGroup>

																		<FormGroup label="Answer" className="mb-0">
																			<TextArea
																				fill
																				rows={5}
																				value={q.answer}
																				disabled={saving}
																				onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
																					setQuestionField(
																						selectedSectionIdx,
																						questionIdx,
																						"answer",
																						e.target.value
																					)
																				}
																			/>
																		</FormGroup>
																	</div>
																</Collapse>
															</div>
														</Card>
													);
												})}
											</div>
										)}
									</>
								)}
							</div>
						</Card>
					</div>
				)}

				<Alert
					isOpen={confirmDeleteSectionIdx !== null}
					cancelButtonText="Cancel"
					confirmButtonText="Delete section"
					intent={Intent.DANGER}
					onCancel={() => setConfirmDeleteSectionIdx(null)}
					onConfirm={() => {
						if (confirmDeleteSectionIdx === null) return;
						deleteSection(confirmDeleteSectionIdx);
						setConfirmDeleteSectionIdx(null);
					}}
				>
					<p>This will permanently remove the section and all its questions.</p>
				</Alert>

				<Alert
					isOpen={!!confirmDeleteQuestion}
					cancelButtonText="Cancel"
					confirmButtonText="Delete question"
					intent={Intent.DANGER}
					onCancel={() => setConfirmDeleteQuestion(null)}
					onConfirm={() => {
						if (!confirmDeleteQuestion) return;
						deleteQuestion(confirmDeleteQuestion.sectionIdx, confirmDeleteQuestion.questionIdx);
						setConfirmDeleteQuestion(null);
					}}
				>
					<p>This will permanently remove the question.</p>
				</Alert>

				<Alert
					isOpen={confirmRefreshDirty}
					cancelButtonText="Cancel"
					confirmButtonText="Refresh and discard changes"
					intent={Intent.WARNING}
					onCancel={() => setConfirmRefreshDirty(false)}
					onConfirm={() => {
						setConfirmRefreshDirty(false);
						fetchFaq();
					}}
				>
					<p>You have unsaved changes. Refreshing will discard them.</p>
				</Alert>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {
		authToken: store.app.auth.authToken,
	};
}

const reduxConnector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(FaqManager);


